import { Component, OnInit, OnDestroy, signal, WritableSignal, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { SecurityService } from '../../services/security.service';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ModalComponent } from "../../shared/modal/modal.component";
import { LoaderService } from '../../services/loader.service';
import { RouteService } from '../../services/route.service';
import { HelpService } from '../../services/help.service';
import { OtpSessionService } from '../../services/otp-session.service';
import { toast } from 'ngx-sonner';

@Component({
  selector: 'app-sign',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModalComponent,
  ],
  templateUrl: './sign.component.html',
  styleUrl: './sign.component.css'
})
export class SignComponent implements OnInit, OnDestroy {

  form: FormGroup;
  seconds: number = 0;
  showModal = signal(true);
  message = signal('');
  intervalId: any;
  showResendButton = false;
  srcImg = 'svg/otp.svg';
  nextRoute: WritableSignal<string> = signal('');
  @ViewChild('submitButton') submitButton!: ElementRef<HTMLButtonElement>;

  constructor(
    private _router: Router,
    private _title: Title,
    private _securityService: SecurityService,
    private _loaderService: LoaderService,
    private _routeService: RouteService,
    private _helpService: HelpService,
    private _cdr: ChangeDetectorRef,
    private _otpSessionService: OtpSessionService
  ){
      this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');

      this._title.setTitle('Onboarding Digital | Firma Vinculación');
      this.form = new FormGroup({
        code1: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code2: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code3: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code4: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code5: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code6: new FormControl('', [Validators.required, Validators.maxLength(1)])
      });
  }

  async ngOnInit() {
    const authRoute = localStorage.getItem('route');
    if(!authRoute) {
      this._router.navigateByUrl('');
    }
    const currentUrl = this._router.url;
    if(authRoute && currentUrl !== authRoute) {
      await this._router.navigateByUrl(authRoute);
    } else {
      const checkpoint11 = await this.getCheckpoint();
      if(checkpoint11) {
        localStorage.setItem('route', this.nextRoute());
        this._router.navigateByUrl(this.nextRoute());
        return;
      }
      this._helpService.show();
      await this.initializeOtpMessage();
      this._loaderService.hide();
      this.startCountdown();
    }
  }

  async getCheckpoint() {
    return new Promise((resolve) => {
      this._securityService.getCheckpoint().subscribe({
        next: (response: any) => {
          const checkpoint = response.checkpoint;
          const checkpointNumber = Number(checkpoint.split('_').pop());
          if(checkpointNumber == 11){
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: (error: any) => {
          console.error(error);
          this._loaderService.hide();
          resolve(true);
        }
      });
    });
  }

  initializeOtpMessage() {
    return new Promise<void>((resolve) => {
      const context = this._otpSessionService.getOtpContext();

      if (context) {
        this.applyOtpMessage(context);
        resolve();
        return;
      }

      this.requestOtp().then(() => resolve());
    });
  }

  getOTP() {
    return this.requestOtp();
  }

  private requestOtp() {
    return new Promise((resolve) => {
      this._securityService.sendOTP().subscribe({
        next: (response: any) => {
          if (this.isSignedCheckpoint(response?.checkpoint)) {
            this._otpSessionService.clearOtpContext();
            localStorage.setItem('route', this.nextRoute());
            this._router.navigateByUrl(this.nextRoute());
          } else {
            this._otpSessionService.saveOtpContext(response);
            this.applyOtpMessage(response);
          }
          resolve(response);
        },
        error: (error: any) => {
          console.error('ERROR sendOTP: ', error);
          this.message.set('No pudimos enviar el código de verificación. Intenta reenviarlo.');
          this.showModal.set(true);
          toast.error('No pudimos enviar el código de verificación. Verifica tu conexión e intenta de nuevo.');
          this._cdr.detectChanges();
          resolve(null);
        }
      });
    });
  }

  private applyOtpMessage(context: { startPhone?: string; lastPhone?: string }): void {
    this.message.set(this._otpSessionService.buildPhoneMessage(context as any));
    this.showModal.set(true);
    this._cdr.detectChanges();
  }

  startCountdown() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    const tick = () => {
      const context = this._otpSessionService.getOtpContext();
      const remaining = context?.otpDate
        ? this._otpSessionService.getRemainingResendSeconds(context.otpDate)
        : 0;

      this.seconds = remaining;
      this.showResendButton = remaining <= 0;
      this._cdr.detectChanges();

      if (remaining <= 0 && this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    };

    tick();
    this.intervalId = setInterval(tick, 1000);
  }

  closeModal() {
    this.showModal.set(false);
    this._cdr.detectChanges(); // Forzar detección de cambios
  }

  // Método para manejar el pegado de código completo
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';

    // Limpiar el texto pegado: solo números y tomar los primeros 6
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);

    if (digits.length > 0) {
      // Distribuir los dígitos en los campos correspondientes
      const codes: any = {};
      for (let i = 0; i < 6; i++) {
        codes[`code${i + 1}`] = digits[i] || '';
      }

      this.form.patchValue(codes);

      // Enfocar el último campo con valor o el último campo si todos están llenos
      const lastFilledIndex = digits.length < 6 ? digits.length : 6;
      const nextInput = document.getElementById(`code${lastFilledIndex}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }

      this._cdr.detectChanges();
    }
  }

  // Método para manejar el input automático entre campos
  onInput(event: any, index: number) {
    const input = event.target;
    const value = input.value;

    // Si se ingresó un valor y no es el último campo, mover al siguiente
    if (value && index < 6) {
      const nextInput = document.getElementById(`code${index + 1}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }
    }

    // Si se presiona backspace y el campo está vacío, retroceder al anterior
    if (event.inputType === 'deleteContentBackward' && !value && index > 1) {
      const prevInput = document.getElementById(`code${index - 1}`);
      if (prevInput) {
        (prevInput as HTMLInputElement).focus();
      }
    }
  }

  // Método para manejar el keydown (mejor control del backspace)
  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    // Si se presiona backspace y el campo está vacío, borrar el anterior y mover el foco
    if (event.key === 'Backspace' && !input.value && index > 1) {
      event.preventDefault();
      const prevInput = document.getElementById(`code${index - 1}`) as HTMLInputElement;
      if (prevInput) {
        this.form.get(`code${index - 1}`)?.setValue('');
        prevInput.focus();
        this._cdr.detectChanges();
      }
    }
  }

  // Método para permitir solo números
  onKeyPress(event: KeyboardEvent) {
    const charCode = event.key;
    if (!/\d/.test(charCode)) {
      event.preventDefault();
    }
  }

  // Obtener el código completo concatenando los 6 dígitos
  getFullCode(): string {
    return [
      this.form.value.code1,
      this.form.value.code2,
      this.form.value.code3,
      this.form.value.code4,
      this.form.value.code5,
      this.form.value.code6
    ].join('');
  }

  private isSignedCheckpoint(checkpoint: string | undefined): boolean {
    const value = String(checkpoint ?? '');
    if (value === 'SIGNED_11') {
      return true;
    }
    const step = Number(value.split('_').pop());
    return Number.isFinite(step) && step >= 11;
  }

  // Verificar si el formulario es válido (todos los campos tienen un dígito)
  isFormValid(): boolean {
    return this.form.valid && this.getFullCode().length === 6;
  }

  onSubmit() {
    if (this.isFormValid()) {
      this.submitButton.nativeElement.disabled = true;
      const fullCode = this.getFullCode();
      this._securityService.validateOTP(fullCode).subscribe({
        next: (response: any) => {
          if (this.isSignedCheckpoint(response?.checkpoint)) {
            this._otpSessionService.clearOtpContext();
            localStorage.setItem('route', this.nextRoute());
            this._router.navigateByUrl(this.nextRoute());
          } else {
            this.submitButton.nativeElement.disabled = false;
            this.srcImg = 'svg/phone-chat.svg';
            this.message.set('Código incorrecto. Por favor, inténtelo de nuevo.');
            this.showModal.set(true);
            this._cdr.detectChanges();
            this.clearForm();
          }
        },
        error: (error: any) => {
          this.submitButton.nativeElement.disabled = false;
          this.srcImg = 'svg/phone-chat.svg';
          this.message.set('Error al validar el código. Por favor, inténtelo de nuevo.');
          this.showModal.set(true);
          this._cdr.detectChanges();
          this.clearForm();
        }
      });
    } else {
      this.message.set('Por favor, complete todos los dígitos del código de verificación.');
      this.showModal.set(true);
      this._cdr.detectChanges(); // Forzar detección de cambios
    }
  }

  clearForm() {
    this.form.patchValue({
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: ''
    });
    // Enfocar el primer campo
    const firstInput = document.getElementById('code1');
    if (firstInput) {
      (firstInput as HTMLInputElement).focus();
    }
    this._cdr.detectChanges(); // Forzar detección de cambios
  }

  resendCode() {
    if (!this._otpSessionService.canResendOtp()) {
      return;
    }

    this.getOTP().then(() => {
      this.startCountdown();
    });
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
